import { formatAsCurrency } from "utils/formatter-utils/currency-formatter";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupCurrencySymbol, getGroupState, setCurrency } from "state/slices/group-slice";
import { useCallback, useEffect, useState } from "react";
import { getRequest, postRequest } from "utils/httpClient";
import { useAuth } from "./auth";
import { getWalletPath, integratedGetWallet } from "utils/backend-path-builders";
import { getIntegratedAppState, IntegratedAppState } from "state/slices/integrated-app-slice";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import store from "state/store";
import { postBalanceMessage } from "utils/integration/iframe-messages";
import { toast } from "react-toastify";
import { integratedPost } from "utils/integrated-app-http-client.ts";
import * as Sentry from "@sentry/react";

interface WalletProps {
  contestEntryId?: string;
  groupId?: string;
}

// A balance fetch can fail for two very different reasons:
//   - a transient network drop (status 0 / fetch rejected) inside the partner
//     Mini App WebView, or a momentarily-missing auth header. The http client
//     already retries these; if it still fails the value simply stays "--.--"
//     and the next render/action re-fetches. Showing a scary toast here is
//     noise, so we suppress it for transient failures.
//   - a real server error (4xx/5xx). Surface a toast and log to Sentry so the
//     failure is actually visible (this hook previously only console.logged).
const isTransientBalanceError = (e: any) => {
  const status = e?.status ?? e?.response?.status;
  // No HTTP status => request never completed (network drop). The auth-skip
  // guard sets status 401 but flags itself as already handled.
  if (e?.name === "MissingIntegrationAuthError") return true;
  return status === undefined || status === 0;
};

const handleBalanceError = (e: any, context: string) => {
  console.log("failed to fetch balance", e);
  if (isTransientBalanceError(e)) return;
  toast.error("Unable to fetch balance.");
  if (!e?.sentryReported) {
    Sentry.captureException(e, {
      tags: { feature: "wallet_balance", balance_context: context },
      extra: { status: e?.status ?? e?.response?.status },
    });
  }
};

// todo rename to use GroupWallet
const useWallet = ({ contestEntryId, groupId: propGroupId }: WalletProps = {}) => {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const [balance, setBalance] = useState<number | undefined>();
  const [walletId, setWalletId] = useState("");
  const [loading, setLoading] = useState(false);
  const group = useAppSelector(getGroupState);
  const groupId = propGroupId || group.id;
  const groupStatus = group.status;
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const state = store.getState();
  const groupSymbol = getGroupCurrencySymbol(state);

  const getWalletData = useCallback(() => {
    switch (integratedAppState.state) {
      case IntegratedAppState.UNDEFINED:
        return;
      case IntegratedAppState.NOT_INTEGRATED:
        if ((auth.signedIn && ((groupId !== "" && groupStatus === UserGroupMembershipStatus.APPROVED) || propGroupId)) || contestEntryId) {
          const walletPath = contestEntryId
            ? `${getWalletPath(groupId)}?contestEntryId=${contestEntryId}`
            : getWalletPath(groupId);

          setLoading(true);
          getRequest(walletPath)
            .then((data) => {
              setBalance(data.current_balance || 0);
              setWalletId(data.wallet_id);
            })
            .catch((e) => {
              handleBalanceError(e, "not_integrated");
            })
            .finally(() => setLoading(false));
        }
        break;
      case IntegratedAppState.INTEGRATED_ANONYMOUS:
        break;
      case IntegratedAppState.INTEGRATED_SESSION_INITIALIZING:
        break;
      case IntegratedAppState.INTEGRATED_SESSION_INITIALIZED:
        setLoading(true);
        integratedPost(integratedGetWallet(contestEntryId), {})
          .then((response) => {
            postBalanceMessage(response.total);
            setBalance(response.total);
            setWalletId(response.wallet_id);
            dispatch(setCurrency(response.currency));
          })
          .catch((e) => {
            handleBalanceError(e, "integrated");
          })
          .finally(() => setLoading(false));
        break;
    }
  }, [
    auth.signedIn,
    groupId,
    groupStatus,
    contestEntryId,
    integratedAppState.state,
    propGroupId
  ]);

  useEffect(() => {
    getWalletData();
  }, [getWalletData]);

  let formattedBalance = groupSymbol + " --.--";
  if (balance !== undefined) {
    formattedBalance = formatAsCurrency(balance!);
  }

  return { balance, walletId, loading, formattedBalance, getWalletData };
};

export default useWallet;
